// The staff app's top bar as data (design v0.6, C3): a few direct links and
// grouped menus. Kept free of React so the rules (what shows, which group is
// active, which menu carries a dot, where the arrow keys go) are tested alone
// in test/nav.test.ts; Nav.tsx draws it.

export type NavItem = {
  to: string;
  label: string;
  description?: string;
  // A count shown on the item; inside a menu it also puts a dot on the menu.
  count?: number;
  // False when the person's roles or the firm's features leave it out.
  show?: boolean;
};

export type NavGroup = { id: string; label: string; items: NavItem[] };

export type NavEntry = { kind: "link"; item: NavItem } | { kind: "menu"; group: NavGroup };

// Items the person can use; menus left with none are dropped.
export function visibleEntries(entries: NavEntry[]): NavEntry[] {
  return entries.flatMap((e): NavEntry[] => {
    if (e.kind === "link") return e.item.show === false ? [] : [e];
    const items = e.group.items.filter((i) => i.show !== false);
    return items.length ? [{ kind: "menu", group: { ...e.group, items } }] : [];
  });
}

const within = (pathname: string, to: string) => pathname === to || pathname.startsWith(`${to}/`);

// The group holding the current page, so its menu is underlined.
export function activeGroup(groups: NavGroup[], pathname: string): string | undefined {
  return groups.find((g) => g.items.some((i) => within(pathname, i.to)))?.id;
}

// A count on any item inside rolls up as a dot on its menu.
export const hasCount = (group: NavGroup) => group.items.some((i) => (i.count ?? 0) > 0);

// Where focus goes in an open menu: arrows move and wrap, Home and End jump.
export function nextIndex(current: number, key: string, length: number): number {
  if (length === 0) return -1;
  switch (key) {
    case "ArrowDown":
      return (current + 1) % length;
    case "ArrowUp":
      return (current - 1 + length) % length;
    case "Home":
      return 0;
    case "End":
      return length - 1;
    default:
      return current;
  }
}

// "50+" when there's more than a page of them.
export const countText = (n: number, more = false) => (more ? `${n}+` : String(n));
